import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from '@xstate/react';
import { useTranslation } from 'react-i18next';

import { useToastMessages } from '@chargify/toasts';
import {
  Card,
  Button,
  Modal,
  Alert,
  Box,
  BodyText,
  Column,
  Toggle,
  Row,
} from '@chargify/custom-ui-components';
import { CreatePaymentProfileContainer } from '../../../../containers/CreatePaymentProfileContainer';
import { CreatePaymentProfileErrorContainer } from '../../../../containers/CreatePaymentProfileErrorContainer';

import { ReplacePaymentProfilesContainer } from '../../../../containers/ReplacePaymentProfilesContainer';

import { useCreatePaymentProfile } from '../../../../hooks';
import { PaymentMethod, paymentProfileSelectors } from '../../../../machines';
import { FORM_ELEMENT_ID } from './constants';
import { useDeviceInfo } from '@chargify/device-provider';

type PaymentProfileContentProps = {
  /**
   * Product catalog identifier
   */
  catalogId: number;
  /**
   * Subsdcription identifier
   */
  subscriptionId: number;
  /**
   * Active payment profile identifier
   */
  activePaymentProfileId: number;
  /**
   * Payment method type
   */
  paymentMethod: PaymentMethod;
  /**
   * Modal display mode
   */
  displayMode: 'layer' | 'window';
  /**
   * Close event handler
   */
  onClose: (successUpdate?: boolean) => void;
  /**
   * Payment profile process event handler
   */
  onProcessingUpdate: (isProcessing: boolean) => void;
};

const Content = ({
  activePaymentProfileId,
  onClose,
  onProcessingUpdate,
  paymentMethod,
  subscriptionId,
  displayMode,
  catalogId,
}: PaymentProfileContentProps) => {
  const toastService = useToastMessages();
  const { t } = useTranslation(['subscriptions-manager', 'payment-profile']);
  const targetElementRef = useRef<HTMLDivElement>(null);
  const [isActivePaymentMethod, setIsActivePaymentMethod] = useState(true);
  const { device } = useDeviceInfo();
  const isOnMobileDevice = ['tablet', 'mobile'].includes(device);

  const onCreateProfileSuccess = () => {
    onClose(true);
    toastService.show({
      level: 'success',
      message: t('success_payment_method_save', { ns: 'payment-profile' }),
    });
  };

  const { isProcessing, isProfileConnectionError, savePaymentMethod, service } =
    useCreatePaymentProfile({
      activePaymentProfileId,
      catalogId,
      subscriptionId,
      formElementId: FORM_ELEMENT_ID,
      paymentMethodType: paymentMethod,
      onCreateSuccess: onCreateProfileSuccess,
      isActivePaymentMethod,
    });

  useEffect(() => {
    onProcessingUpdate(isProcessing);
  }, [isProcessing]);

  const paymentMethodsTranslations = {
    creditCard: t('payment_profile.card'),
    bankAccount: t('payment_profile.bank_account'),
  };

  const isPaymentProfilesReplacementError = useSelector(
    service,
    paymentProfileSelectors.getPaymentProfilesReplacementError
  );

  const isError = isProfileConnectionError || isPaymentProfilesReplacementError;
  const modalTitle = t('payment_profile.new_payment_method_title', {
    paymentMethod: paymentMethodsTranslations[paymentMethod],
  });

  return (
    <Card
      ref={targetElementRef}
      border={displayMode === 'layer' ? 'sharp' : 'rounded'}
    >
      <Card.Header>
        <Modal.Title onClose={onClose} title={modalTitle} />
      </Card.Header>
      <Card.Body>
        <CreatePaymentProfileErrorContainer paymentProfileRef={service} />
        {!isProfileConnectionError && (
          <>
            {!isPaymentProfilesReplacementError ? (
              <>
                <CreatePaymentProfileContainer
                  formElementId={FORM_ELEMENT_ID}
                  paymentMethodType={paymentMethod}
                  paymentProfileRef={service}
                  targetElementRef={targetElementRef}
                />
                <Box paddingTop={6} paddingBottom={5}>
                  <Alert color={'success'}>
                    <Alert.TextMessage fontWeight={600}>
                      {t('payment_profile.data_is_stored_safely')}
                    </Alert.TextMessage>
                  </Alert>
                </Box>
              </>
            ) : null}
            <Box paddingTop={6} paddingBottom={5}>
              <Row>
                <Column span={isOnMobileDevice ? 18 : 21}>
                  <BodyText>
                    {t('payment_profile.make_active_payment_method')}
                  </BodyText>
                </Column>
                <Column span={isOnMobileDevice ? 6 : 3}>
                  <Toggle
                    isOn={isActivePaymentMethod}
                    onChange={setIsActivePaymentMethod}
                  />
                </Column>
              </Row>
            </Box>
            <ReplacePaymentProfilesContainer paymentProfileRef={service} />
          </>
        )}
      </Card.Body>
      <Card.Footer>
        <Button.Group alignment="right">
          <Button
            color="secondary"
            label={t('close', { ns: 'common' })}
            onClick={() => onClose(false)}
          />
          {!isError && (
            <Button
              label={t('payment_profile.save_payment_method', {
                ns: 'subscriptions-manager',
                paymentMethod: paymentMethodsTranslations[paymentMethod],
              })}
              disabled={isProcessing}
              isLoading={isProcessing}
              loadingText={t('processing', { ns: 'common' })}
              onClick={() => savePaymentMethod()}
            />
          )}
        </Button.Group>
      </Card.Footer>
    </Card>
  );
};

export default Content;
